// eslint-disable-next-line import/no-extraneous-dependencies
import { AxiosResponse, AxiosInstance } from 'axios';

import {
  IGNORED_NONCE,
  SAVE_STATE_MAX_DELAY_MS,
  // SAVE_STATE_MAX_DELAY_MS,
  urls,
} from 'config/constants';
import { addExternalMarkdownLink } from 'lib/display/markdown';
import { capitalizeFirstLetter } from 'lib/string';
import { fetchAndDispatchNotices } from 'reduxState/notices/noticesReducerUtils';
import { handleRestrictedRegion } from 'reduxState/region/utils';
import { staticDispatch } from 'reduxState/store';
import { removeWallet } from 'reduxState/wallet/walletReducer';

import {
  ERROR_USER_EXISTS,
  ExposedErrorCodes,
  KatanaApiError,
} from './apiPatchConstants';
// import { staticDispatch } from 'reduxState/store';
// import { removeWallet } from 'reduxState/wallet/walletReducer';

let wasGeoRestricted: boolean;

async function checkGeoHeaders(headers: AxiosResponse['headers']) {
  /**
   * Renders to either 'false' or the restricted region in the case the user is
   * associated with a restricted region based on their IP address.  These restrictions
   * generally are more specific than the `geoHeader` value which is largely on a country
   * basis.  For example, New York State, Washington State, Crimea.
   */
  const geoRestrictionHeader =
    headers['kp-geo-restricted'] ?? headers['KP-Geo-Restricted'] ?? 'false';

  const isGeoRestricted = geoRestrictionHeader !== 'false';

  if (isGeoRestricted !== wasGeoRestricted) {
    wasGeoRestricted = isGeoRestricted;
    await handleRestrictedRegion(
      isGeoRestricted ? geoRestrictionHeader : false
    );
    // TODO: circular dependency: move logic from patch layer
    await fetchAndDispatchNotices();
  }
}

const UNKNOWN_ERROR = 'Unknown Error';

async function handleErrorResponse(
  response?: AxiosResponse,
  result: { code?: string; message?: string } = {},
  stack?: string
) {
  console.warn({
    errorCode: result.code,
    errorMessage: result.message,
    errorStatus: response?.statusText,
  });
  console.log(result);
  switch (result.code) {
    case ERROR_USER_EXISTS:
      throw new KatanaApiError(
        'An account with the given email already exists',
        ERROR_USER_EXISTS
      );
    case 'EMAIL_UNVERIFIED':
      // the user has not verified their email yet and has made requests
      // which require it
      console.warn('User Email Verification Required');
      break;
    case 'INVALID_WALLET_AUTH_TOKEN':
      console.warn('API Session missing for Wallet - locking wallet');
      staticDispatch(removeWallet());
      break;
    // This is happening when trying to requist Wallet resource without having session active
    // Possible Repro? Happening randomly - when unlocking same wallet multiple times (diff staging url)
    case 'REQUIRED_API_KEY':
      console.warn('Invalid Wallet Authentication - locking wallet');
      staticDispatch(removeWallet());
      // This error would cause failed API requests, and UI possibly in broken state
      // Best option is to do full reload once Redux is propagated
      setTimeout(() => window.location.reload(), SAVE_STATE_MAX_DELAY_MS * 1.2);
      break;
    case 'UNAUTHORIZED': {
      break;
    }
  }

  const errorMessage =
    result.code === 'INVALID_NONCE_TIMING'
      ? `Unable to complete the requested action.  ${addExternalMarkdownLink(
          'Please confirm',
          urls.katanaDocsClockSync
        )} that your computer clock is set to the correct time.`
      : result.message ||
        (result.code ? `Error with code ${result.code}` : undefined) ||
        response?.statusText ||
        UNKNOWN_ERROR;

  if (errorMessage === UNKNOWN_ERROR && stack) {
    // We want to display original stack to trace error
    console.warn(stack);
  }

  throw new KatanaApiError(
    capitalizeFirstLetter(errorMessage),
    result?.code as ExposedErrorCodes
  );
}

export function patchClientToIdexInternal(client: { axios: AxiosInstance }) {
  client.axios.interceptors.request.use(config => {
    // Remove any nonce which matches the `IGNORED_NONCE` string.
    // Part 1: Remove it from request body
    if (config.data?.nonce === IGNORED_NONCE) {
      delete config.data.nonce;
    }

    // Part 2: Remove it from url path
    const urlParams = config.params;
    if (!urlParams) {
      return config;
    }
    if (urlParams instanceof URLSearchParams) {
      if (urlParams.get('nonce') === IGNORED_NONCE) {
        urlParams.delete('nonce');
      }
    } else {
      // console.log({ urlParams });
      console.error(
        `[patchClient] urlSearchParams not expected type on url:${config?.url}`
      );
    }
    return config;
  });

  client.axios.interceptors.response.use(
    async response => {
      // console.log('Response: ', response);
      if (!response.headers) {
        response.headers = {} as AxiosResponse['headers'];
      }
      try {
        if (response.status === 200 || response.status === 204) {
          await checkGeoHeaders(response.headers);
        }

        // NO CONTENT
        if (response.status === 204) {
          return response;
        }
      } catch (err) {
        console.error(`[API PARSE ERROR, url=${response?.config?.url}]`, err);
        throw err;
      }

      // console.log(response.status, ' RESPONSE STATUS ', response.data);
      if (
        response.status >= 400 ||
        (response?.data?.code && response?.data?.message)
      ) {
        await handleErrorResponse(response, response.data);
      }

      return response;
    },
    error => {
      // console.error('Error Happened ', error);
      return handleErrorResponse(
        error?.response,
        error?.response?.data,
        error?.stack
      );
    }
  );
}
